// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------
// Thin fetch wrapper. Two responsibilities:
//
//   * attach the Cognito ID token as a bearer credential, refreshed by Amplify
//     as needed, on every call
//   * turn an error response into an Error carrying `.status`, `.fields` and
//     `.incidentId`, so a form can mark its invalid inputs and a user can
//     quote an id to support
//
// There is no CSRF token here and no `credentials: 'include'`. Nothing about
// this request is attached by the browser on its own initiative — the header
// below is set deliberately by this code — so there is no cross-site request
// to forge and nothing for a double-submit token to add.
// ---------------------------------------------------------------------------

import { idToken } from '../auth/cognito';

const BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export class ApiError extends Error {
  constructor(message, { status, fields, incidentId, body } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fields = fields;
    this.incidentId = incidentId;
    this.body = body;
  }

  /** True when the session is gone and the app should return to the sign-in screen. */
  get isAuthError() {
    return this.status === 401;
  }
}

async function request(method, path, { body, raw, query, signal } = {}) {
  let url = `${BASE}${path}`;

  if (query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '' || v === false) continue;
      params.set(k, Array.isArray(v) ? v.join(',') : String(v));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  const headers = {};
  const token = await idToken();
  if (token) headers.authorization = `Bearer ${token}`;

  let payload;
  if (raw !== undefined) {
    payload = raw;
    headers['content-type'] = 'application/octet-stream';
  } else if (body !== undefined) {
    payload = JSON.stringify(body);
    headers['content-type'] = 'application/json';
  }

  let res;
  try {
    res = await fetch(url, { method, headers, body: payload, signal });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    // Distinguish "the network failed" from "the server said no" — they need
    // different messages and only one is worth retrying.
    throw new ApiError('Could not reach the support service. Check your connection.', { status: 0 });
  }

  if (res.status === 204) return null;

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const message =
      (data && typeof data === 'object' && data.error) ||
      `Request failed with status ${res.status}.`;
    throw new ApiError(message, {
      status: res.status,
      fields: data?.fields,
      incidentId: data?.incidentId,
      body: data,
    });
  }

  return data;
}

/** Binary fetch for attachment download. Returns a Blob. */
async function download(path) {
  const token = await idToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const text = await res.text();
    let data = null;
    try {
      data = JSON.parse(text);
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(data?.error || 'Download failed.', {
      status: res.status,
      incidentId: data?.incidentId,
    });
  }
  return res.blob();
}

const get = (path, query, opts) => request('GET', path, { query, ...opts });
const post = (path, body, opts) => request('POST', path, { body, ...opts });
const patch = (path, body, opts) => request('PATCH', path, { body, ...opts });
const put = (path, body, opts) => request('PUT', path, { body, ...opts });
const del = (path, opts) => request('DELETE', path, opts);

/**
 * The API surface, named by domain rather than by HTTP verb so a screen reads
 * as what it does.
 */
export const api = {
  // --- Session ---
  // Normal sign-in is Cognito's. Recovery confirmation goes through the API
  // so it can clear the old TOTP enrollment after Cognito accepts the emailed
  // one-time reset code.
  me: () => get('/auth/me'),
  meta: () => get('/meta'),
  confirmPasswordReset: (body) => post('/auth/password-reset/confirm', body),

  // --- Dashboards ---
  overview: () => get('/overview'),

  // --- Tickets ---
  tickets: (query, opts) => get('/tickets', query, opts),
  ticket: (id) => get(`/tickets/${id}`),
  queueCounts: () => get('/tickets/queue-counts'),
  priorityPreview: (body) => post('/tickets/priority-preview', body),
  createTicket: (body) => post('/tickets', body),
  addMessage: (id, body) => post(`/tickets/${id}/messages`, body),
  setStatus: (id, body) => post(`/tickets/${id}/status`, body),
  setPriority: (id, body) => post(`/tickets/${id}/priority`, body),
  assign: (id, body) => post(`/tickets/${id}/assign`, body),
  escalate: (id, body) => post(`/tickets/${id}/escalate`, body),
  ticketContext: (id) => get(`/tickets/${id}/context`),
  assistant: (id) => get(`/tickets/${id}/assistant`),
  replyDraft: (id, kind) => get(`/tickets/${id}/reply-draft`, { kind }),

  // --- Attachments ---
  upload: (ticketId, file, { internal = false } = {}) =>
    file.arrayBuffer().then((buf) =>
      request('POST', `/tickets/${ticketId}/attachments`, {
        raw: buf,
        query: { filename: file.name, internal: internal || undefined },
      }),
    ),
  downloadAttachment: (id) => download(`/attachments/${id}/download`),
  attachmentLog: (id, query) => get(`/attachments/${id}/log`, query),

  // --- Incidents ---
  incidents: (query) => get('/incidents', query),
  incident: (id) => get(`/incidents/${id}`),
  createIncident: (body) => post('/incidents', body),
  updateIncident: (id, body) => patch(`/incidents/${id}`, body),
  addIncidentUpdate: (id, body) => post(`/incidents/${id}/updates`, body),
  linkTickets: (id, ticket_ids) => post(`/incidents/${id}/link`, { ticket_ids }),
  unlinkTickets: (id, ticket_ids) => post(`/incidents/${id}/unlink`, { ticket_ids }),
  incidentCandidates: (id) => get(`/incidents/${id}/candidates`),

  // --- Escalations ---
  escalations: (query) => get('/escalations', query),
  acknowledgeEscalation: (id) => post(`/escalations/${id}/acknowledge`),
  resolveEscalation: (id, outcome) => post(`/escalations/${id}/resolve`, { outcome }),

  // --- Knowledge base ---
  kb: (query) => get('/kb', query),
  article: (id) => get(`/kb/${id}`),
  suggestArticles: (text, limit) => post('/kb/suggest', { text, limit }),
  markArticleHelpful: (id) => post(`/kb/${id}/helpful`),
  createArticle: (body) => post('/kb', body),
  updateArticle: (id, body) => patch(`/kb/${id}`, body),
  deleteArticle: (id) => del(`/kb/${id}`),

  // --- Product feedback ---
  feedback: (query, opts) => get('/feedback', query, opts),
  setFeedbackState: (id, state) => post(`/feedback/${id}/state`, { state }),

  // --- Monitoring ---
  slaMonitor: (query) => get('/sla-monitor', query),
  analytics: (query) => get('/analytics', query),
  workload: () => get('/workload'),
  audit: (query) => get('/audit', query),
  search: (q) => get('/search', { q }),

  // --- Notifications ---
  notifications: (query) => get('/notifications', query),
  markNotificationsRead: (body) => post('/notifications/read', body),
  updateNotificationPrefs: (prefs) => patch('/me/notification-prefs', { prefs }),

  // --- Clinic log access (Monitoring & Diagnostics) ---
  // Proxied by this server onto the CliniqVoice API, which owns the grants.
  // No connect/disconnect: the CliniqVoice API accepts this console's own
  // tokens, so there is no second account to attach and nothing stored to
  // detach. Status answers one question — is a CliniqVoice environment
  // configured on the server at all.
  platformStatus: (opts) => get('/platform/status', undefined, opts),
  platformClinics: (q, opts) => get('/platform/clinics', { q }, opts),
  accessRequests: (query, opts) => get('/platform/access-requests', query, opts),
  createAccessRequest: (body) => post('/platform/access-requests', body),
  accessRequestActivity: (id, opts) => get(`/platform/access-requests/${id}/activity`, undefined, opts),
  clinicLogs: (clinicId, query, opts) => get(`/platform/clinics/${clinicId}/logs`, query, opts),
  exportClinicLogs: (clinicId, body) => post(`/platform/clinics/${clinicId}/logs/export`, body),
  // No packId argument on purpose: the server reads it from the ticket's own
  // billing.minute_pack_request event, so the browser cannot name a pack.
  approvePack: (ticketId) => post(`/platform/tickets/${ticketId}/approve-pack`, {}),
  // Voice-agent provisioning. The GET reports whether a key is stored, never
  // the key itself; the POST hands both fields to the product API, which
  // verifies them with ElevenLabs before anything is written.
  agentCredentials: (clinicId, opts) =>
    get(`/platform/clinics/${clinicId}/agent-credentials`, undefined, opts),
  saveAgentCredentials: (clinicId, body) =>
    post(`/platform/clinics/${clinicId}/agent-credentials`, body),
  // Raised from the ticket it is for: files the request and posts the
  // "please approve this" message onto the thread in one call.
  requestTicketClinicAccess: (ticketId, body) => post(`/tickets/${ticketId}/clinic-access`, body),

  // --- Catalog / administration ---
  // Support terms per subscription tier. There is no create or delete: the
  // tiers mirror the CliniqVoice subscriptions, so inventing one here would
  // mean inventing a subscription.
  plans: () => get('/plans'),
  updatePlan: (id, body) => patch(`/plans/${id}`, body),
  slaPolicies: () => get('/sla-policies'),
  saveSlaPolicy: (body) => put('/sla-policies', body),
  deleteSlaPolicy: (id) => del(`/sla-policies/${id}`),
  priorityRules: () => get('/priority-rules'),
  publishPriorityRules: (body) => post('/priority-rules', body),
  categories: () => get('/categories'),
  createCategory: (body) => post('/categories', body),
  updateCategory: (id, body) => patch(`/categories/${id}`, body),
  teams: () => get('/teams'),
  createTeam: (body) => post('/teams', body),
  updateTeam: (id, body) => patch(`/teams/${id}`, body),
  users: (query) => get('/users', query),
  createUser: (body) => post('/users', body),
  updateUser: (id, body) => patch(`/users/${id}`, body),
  deleteUser: (id) => del(`/users/${id}`),
  resetUserPassword: (id) => post(`/users/${id}/reset-password`, {}),
  resetUserMfa: (id) => post(`/users/${id}/reset-mfa`, {}),
  agents: () => get('/agents'),
  organizations: (query) => get('/organizations', query),
  organization: (id) => get(`/organizations/${id}`),
  updateOrganization: (id, body) => patch(`/organizations/${id}`, body),
  notificationRules: () => get('/notification-rules'),
  saveNotificationRule: (body) => put('/notification-rules', body),
  deleteNotificationRule: (id) => del(`/notification-rules/${id}`),
  settings: () => get('/settings'),

  // --- Saved filters ---
  savedFilters: (scope) => get('/saved-filters', { scope }),
  saveFilter: (body) => post('/saved-filters', body),
  deleteFilter: (id) => del(`/saved-filters/${id}`),
};

export default api;
